import type { ServerTraceDiagnosticsResult } from "@cmmd-forge/contracts";

import type { StatusTone } from "./DiagnosticsSettings.types";

/**
 * How much of the trace file the server actually managed to read. A panel that
 * cannot tell these apart shows "0 failures" for all four, which reads as
 * healthy when three of them mean "we do not know".
 */
export type TraceEvidenceState =
  | "unavailable" // no trace file at all
  | "unreadable" // a file exists but reading it failed
  | "partial" // some rotated files read, at least one did not
  | "empty" // read cleanly, nothing in it yet
  | "complete";

export function traceEvidenceState(result: ServerTraceDiagnosticsResult): TraceEvidenceState {
  if (result.error?.kind === "trace-file-read-failed") {
    return result.recordCount > 0 ? "partial" : "unreadable";
  }
  if (result.error?.kind === "trace-file-not-found") return "unavailable";
  if (result.partialFailure === true) return "partial";
  return result.recordCount === 0 ? "empty" : "complete";
}

/**
 * The one line at the top. Leads with the thing that needs a decision —
 * failures first, then interruptions, then slowness — and falls back to a
 * statement of coverage rather than a bare zero.
 */
export function traceHeadline(result: ServerTraceDiagnosticsResult): string {
  const state = traceEvidenceState(result);
  if (state === "unavailable") return "No trace file has been written yet.";
  if (state === "unreadable") return "The trace file could not be read.";

  const spans = `${result.recordCount.toLocaleString()} ${result.recordCount === 1 ? "span" : "spans"}`;
  if (result.recordCount === 0) return "The trace file is empty.";
  if (result.failureCount > 0) {
    return `${result.failureCount.toLocaleString()} of ${spans} failed.`;
  }
  if (result.interruptionCount > 0) {
    return `No failures in ${spans}, but ${result.interruptionCount.toLocaleString()} were interrupted.`;
  }
  if (result.slowSpanCount > 0) {
    return `No failures in ${spans}; ${result.slowSpanCount.toLocaleString()} ran over ${formatDurationMs(result.slowSpanThresholdMs)}.`;
  }
  return `No failures in ${spans}.`;
}

/**
 * A badge only when the read itself was incomplete.
 *
 * Health is already stated twice — in the headline sentence and in the
 * coloured counts — so a third badge repeating it earns nothing, and the
 * obvious wording for it ("complete") collides with the failure tone: a red
 * pill reading "complete" says the opposite of what it means. What is *not*
 * said anywhere else is that the numbers may be undercounts.
 */
export function traceCoverageWarning(
  result: ServerTraceDiagnosticsResult,
): { readonly label: string; readonly tone: StatusTone } | null {
  switch (traceEvidenceState(result)) {
    case "unavailable":
      return { label: "no trace file", tone: "muted" };
    case "unreadable":
      return { label: "unreadable", tone: "error" };
    case "partial":
      return { label: "partial read", tone: "warning" };
    default:
      return null;
  }
}

export function formatDurationMs(durationMs: number): string {
  if (!Number.isFinite(durationMs)) return "—";
  if (durationMs < 1) return "<1ms";
  if (durationMs < 1_000) return `${Math.round(durationMs)}ms`;
  if (durationMs < 60_000) return `${(durationMs / 1_000).toFixed(durationMs < 10_000 ? 2 : 1)}s`;
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = Math.round((durationMs % 60_000) / 1_000);
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

/**
 * The span window, which is what makes every count above interpretable: ten
 * failures over four minutes and ten over four days are different problems.
 */
export function formatTraceWindow(result: ServerTraceDiagnosticsResult): string | null {
  if (result.firstSpanAt === undefined || result.lastSpanAt === undefined) return null;
  const from = new Date(result.firstSpanAt).getTime();
  const to = new Date(result.lastSpanAt).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null;
  return formatDurationMs(to - from);
}

/**
 * `Cause.pretty` output is multi-line and starts with the interesting part.
 * The list shows the first line; the full cause stays available on the row.
 */
export function firstCauseLine(cause: string): string {
  const [first = ""] = cause.split("\n");
  return first.trim().length > 0 ? first.trim() : cause.trim();
}

export interface TraceSummaryStat {
  readonly label: string;
  readonly value: string;
  readonly tone: StatusTone;
}

export function traceSummaryStats(
  result: ServerTraceDiagnosticsResult,
): ReadonlyArray<TraceSummaryStat> {
  const stats: Array<TraceSummaryStat> = [
    { label: "Spans", value: result.recordCount.toLocaleString(), tone: "muted" },
    {
      label: "Failed",
      value: result.failureCount.toLocaleString(),
      tone: result.failureCount > 0 ? "error" : "muted",
    },
    {
      label: "Interrupted",
      value: result.interruptionCount.toLocaleString(),
      tone: result.interruptionCount > 0 ? "warning" : "muted",
    },
    {
      label: `Over ${formatDurationMs(result.slowSpanThresholdMs)}`,
      value: result.slowSpanCount.toLocaleString(),
      tone: result.slowSpanCount > 0 ? "warning" : "muted",
    },
  ];
  // Only shown when non-zero: a permanent "Unparsed 0" is noise, but a
  // non-zero one means the counts beside it are undercounts.
  if (result.parseErrorCount > 0) {
    stats.push({
      label: "Unparsed lines",
      value: result.parseErrorCount.toLocaleString(),
      tone: "warning",
    });
  }
  return stats;
}
